import { Link } from "react-router-dom";
import { SERVER_IP } from "@/lib/api";
import { Youtube, Instagram, MessageCircle } from "lucide-react";
import Logo from "./Logo";

const SOCIAL_LINKS = {
  youtube: "https://lostdrkc.in/youtube",
  instagram: "https://lostdrkc.in/instagram",
  whatsapp: "https://lostdrkc.in/whatsapp",
  discord: "https://lostdrkc.in/discord",
};

// Custom SVG component for Discord icon matching Lucide icon sizing
function DiscordIcon({ size = 18, className = "" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
    >
      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.061 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.893.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.028zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
    </svg>
  );
}

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer
      className="relative z-10 mt-32 border-t border-white/10 bg-black"
      data-testid="site-footer"
    >
      <div className="max-w-7xl mx-auto px-6 py-16 grid gap-10 md:grid-cols-4">

        {/* BRAND */}
        <div>
          <Logo className="h-12 mb-4" />

          <p className="text-sm text-white/60 leading-relaxed">
            India's ultimate Minecraft network. Multiple game modes,
            custom mechanics, competitive events and a community built
            for every kind of player.
          </p>
        </div>

        {/* NETWORK */}
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#FFBF00] mb-4">
            Network
          </div>

          <ul className="space-y-2 text-sm text-white/70">
            <li>
              <Link to="/servers" className="hover:text-[#FFBF00] transition-colors">
                Servers
              </Link>
            </li>

            <li>
              <Link to="/servers" className="hover:text-[#FFBF00] transition-colors">
                Survival
              </Link>
            </li>

            <li>
              <Link to="/servers" className="hover:text-[#FFBF00] transition-colors">
                Lifesteal
              </Link>
            </li>

            <li>
              <Link to="/servers" className="hover:text-[#FFBF00] transition-colors">
                PlayZone
              </Link>
            </li>

            <li>
              <Link to="/servers" className="hover:text-[#FFBF00] transition-colors">
                Parkour
              </Link>
            </li>
          </ul>
        </div>

        {/* COMMUNITY */}
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#FFBF00] mb-4">
            Community
          </div>

          <ul className="space-y-2 text-sm text-white/70">
            <li>
              <Link to="/leaderboards" className="hover:text-[#FFBF00] transition-colors">
                Leaderboards
              </Link>
            </li>

            <li>
              <Link to="/events" className="hover:text-[#FFBF00] transition-colors">
                Events
              </Link>
            </li>

            <li>
              <Link to="/builds" className="hover:text-[#FFBF00] transition-colors">
                Builds Gallery
              </Link>
            </li>

            <li>
              <Link to="/about" className="hover:text-[#FFBF00] transition-colors">
                About Us
              </Link>
            </li>

            <li>
              <Link to="/contact" className="hover:text-[#FFBF00] transition-colors">
                Contact
              </Link>
            </li>
          </ul>
        </div>

        {/* PLAY NOW */}
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#FFBF00] mb-4">
            Play Now
          </div>

          <div className="rounded-lg glass p-4">
            <div className="text-[11px] uppercase tracking-widest text-white/50">
              Java / Bedrock IP
            </div>

            <div className="font-mono text-[#FFBF00] text-lg">
              {SERVER_IP} (19132)
            </div>

            <div className="text-[11px] uppercase tracking-widest text-white/50 mt-3">
              Version
            </div>

            <div className="font-mono text-sm">
              1.7.2 — letest
            </div>
          </div>

          <div className="flex items-center gap-3 mt-4 text-white/70">

            {/* YouTube */}
            <a
              href={SOCIAL_LINKS.youtube}
              target="_blank"
              rel="noreferrer"
              aria-label="YouTube"
              className="hover:text-[#FFBF00] transition-colors"
            >
              <Youtube size={18} />
            </a>

            {/* Instagram */}
            <a
              href={SOCIAL_LINKS.instagram}
              target="_blank"
              rel="noreferrer"
              aria-label="Instagram"
              className="hover:text-[#FFBF00] transition-colors"
            >
              <Instagram size={18} />
            </a>

            {/* WhatsApp */}
            <a
              href={SOCIAL_LINKS.whatsapp}
              target="_blank"
              rel="noreferrer"
              aria-label="WhatsApp"
              className="hover:text-[#FFBF00] transition-colors"
            >
              <MessageCircle size={18} />
            </a>

            {/* Discord */}
            <a
              href={SOCIAL_LINKS.discord}
              target="_blank"
              rel="noreferrer"
              aria-label="Discord"
              className="hover:text-[#FFBF00] transition-colors"
            >
              <DiscordIcon size={18} />
            </a>

          </div>
        </div>
      </div>

      {/* COPYRIGHT BAR */}
      <div className="border-t-2 border-[#FFBF00]/30">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-white/50">

          {/* LEFT */}
          <div>
            © 2024–{currentYear}{" "}
            <span className="text-[#FFBF00] font-semibold">
              LOSTDRKC
            </span>
            . All rights reserved.
          </div>

          {/* RIGHT */}
          <div className="font-hero text-sm text-white/70">
            <span className="text-white">
              LOSTDC NETWORK
            </span>

            <span className="text-white/30 mx-2">
              ·
            </span>

            <span className="text-[#FFBF00]">
              POWERED BY LOSTDRKC
            </span>
          </div>

        </div>
      </div>
    </footer>
  );
}